import React, { useState } from 'react';
import './PricingPage.css';
import ComingSoonPage from './ComingSoonPage';

const PricingPage = ({ currentTier = 'lite', onSelectTier, onClose }) => {
  const [showComingSoon, setShowComingSoon] = useState(null); // 'wealth' or 'elite'

  const tiers = [
    {
      id: 'lite',
      name: 'Lite',
      price: 'Free',
      priceSubtext: 'Forever',
      tagline: 'Play & Learn',
      target: 'DIY learners & explorers',
      comingSoon: false,
      features: [
        { text: 'Allocation sandbox with historical data', included: true },
        { text: 'Category mix & overlap visualizations', included: true },
        { text: 'Drawdown analysis', included: true },
        { text: '"What-if" scenario tools', included: true },
        { text: 'Basic portfolio optimization', included: true },
        { text: 'SEBI RIA advisor sessions', included: false },
        { text: 'Goal-based allocation plans', included: false },
        { text: 'Continuous monitoring & alerts', included: false },
        { text: 'Multi-asset view', included: false },
        { text: 'Family portfolio (multiple PANs)', included: false },
      ],
      buttonText: currentTier === 'lite' ? 'Current Plan' : 'Downgrade',
      highlighted: false,
      theme: 'lite',
    },
    {
      id: 'wealth',
      name: 'Wealth',
      price: '₹1,499',
      priceSubtext: '/year',
      tagline: 'Expert Guidance',
      target: '₹5-50L portfolios',
      comingSoon: true,
      badge: 'Most Popular',
      features: [
        { text: 'Everything in Lite', included: true, isHeader: true },
        { text: '1-2 structured sessions/year with SEBI RIA', included: true, highlight: true },
        { text: 'Goal-based allocation plans', included: true },
        { text: 'AI pre-analysis pack for advisor sessions', included: true },
        { text: 'Priority email support', included: true },
        { text: 'Professional PDF reports', included: true },
        { text: 'Continuous monitoring & alerts', included: false },
        { text: 'Multi-asset view (MF + Gold + RE)', included: false },
        { text: 'Family portfolio view', included: false },
        { text: 'Priority advisor access on call', included: false },
      ],
      buttonText: currentTier === 'wealth' ? 'Current Plan' : currentTier === 'elite' ? 'Downgrade' : 'Upgrade',
      highlighted: true,
      theme: 'wealth',
    },
    {
      id: 'elite',
      name: 'Elite',
      price: '₹14,999',
      priceSubtext: '/year',
      tagline: 'Always-On Desk',
      target: '₹50L+ portfolios',
      comingSoon: true,
      features: [
        { text: 'Everything in Wealth', included: true, isHeader: true },
        { text: 'Continuous monitoring + alerts', included: true, highlight: true },
        { text: 'Multi-asset view (MFs + commodities + RE + gold)', included: true },
        { text: 'Priority access to advisors on call', included: true, highlight: true },
        { text: 'Family portfolio view (multiple PANs)', included: true },
        { text: 'Advanced stress testing', included: true },
        { text: 'Custom allocation constraints', included: true },
        { text: 'Dedicated relationship manager', included: true },
        { text: 'Quarterly portfolio review calls', included: true },
        { text: 'White-label reports', included: true },
      ],
      buttonText: currentTier === 'elite' ? 'Current Plan' : 'Upgrade',
      highlighted: false,
      theme: 'elite',
    },
  ];

  const handleSelectTier = (tierId) => {
    if (onSelectTier && tierId !== currentTier) {
      onSelectTier(tierId);
    }
  };

  // Show Coming Soon page if selected
  if (showComingSoon) {
    return (
      <ComingSoonPage
        tier={showComingSoon}
        onBack={() => setShowComingSoon(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="pricing-page">
      <div className="pricing-overlay" onClick={onClose}></div>
      <div className="pricing-container">
        <button className="pricing-close" onClick={onClose}>×</button>

        <div className="pricing-header">
          <h1>Choose Your Plan</h1>
          <p>Unlock the power of intelligent portfolio allocation</p>
        </div>

        <div className="pricing-cards">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`pricing-card ${tier.theme} ${tier.highlighted ? 'highlighted' : ''} ${currentTier === tier.id ? 'current' : ''}`}
            >
              {tier.badge && <div className="pricing-badge">{tier.badge}</div>}

              <div className="pricing-card-header">
                <h2>{tier.name}</h2>
                <p className="pricing-tagline">{tier.tagline}</p>
              </div>

              <div className="pricing-price">
                <span className="price-amount">{tier.price}</span>
                <span className="price-subtext">{tier.priceSubtext}</span>
              </div>

              <p className="pricing-target">{tier.target}</p>

              <ul className="pricing-features">
                {tier.features.map((feature, index) => (
                  <li
                    key={index}
                    className={`${feature.included ? 'included' : 'excluded'} ${feature.isHeader ? 'header' : ''} ${feature.highlight ? 'highlight' : ''}`}
                  >
                    <span className="feature-icon">
                      {feature.included ? '✓' : '—'}
                    </span>
                    <span className="feature-text">{feature.text}</span>
                  </li>
                ))}
              </ul>

              <div className="pricing-actions">
                {tier.comingSoon ? (
                  <button
                    className={`pricing-button coming-soon-btn ${tier.theme}`}
                    onClick={() => setShowComingSoon(tier.id)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Coming Soon
                  </button>
                ) : (
                  <button
                    className={`pricing-button ${currentTier === tier.id ? 'current' : ''}`}
                    onClick={() => handleSelectTier(tier.id)}
                    disabled={currentTier === tier.id}
                  >
                    {tier.buttonText}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="pricing-footer">
          <p>All plans include secure data handling and SEBI compliance</p>
          <p className="pricing-guarantee">14-day money-back guarantee on paid plans</p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
